import { cn } from '@/lib/utils'
// src/components/layout/AppLayout.tsx
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LegacyInstructionsRedirect } from './LegacyInstructionsRedirect'
import AppSidebar from './AppSidebar'
import { SidebarProvider } from '@teach/ui'
import { MobileSidebarTrigger } from './sidebar/MobileSidebarTrigger'
import { MOBILE_MENU_PB_CLASS } from './pageHeight'
import Dashboard from '@/pages/Dashboard'
import Editor from '@/pages/Editor'
import Catalog from '@/pages/Catalog'
import AcademyCourseRedirect from '@/pages/AcademyCourseRedirect'
import CourseStats from '@/pages/CourseStats'
import CourseSettings from '@/pages/CourseSettings'
import NotFound from '@/pages/NotFound'
import { InstructionsAdmin } from '@/pages/admin/InstructionsAdmin'
import { InstructionEdit } from '@/pages/admin/InstructionEdit'
import { useBrowserChromeColor } from './useBrowserChromeColor'
import { AdminPage } from '@/features/admin/components/AdminPage'
import { AdminOnly } from '@/features/admin/components/AdminOnly'
import { SkillsPage } from '@/features/skills/SkillsPage'
import { settingsRoutes } from '@/features/settings/settingsRoutes'

/** Совпадает с Tailwind `lg` — ниже него меню выезжает поверх. */
const SIDEBAR_SHEET_BELOW = 1024

const EDITOR_ROUTE_RE = /^\/editor\/[^/]+$/

export const AppLayout = () => {
  const { pathname } = useLocation()
  const hideSidebar = EDITOR_ROUTE_RE.test(pathname)
  useBrowserChromeColor(hideSidebar)

  return (
    // Боковое меню на десктопе всегда раскрыто: open зафиксирован, поэтому и
    // сочетание Cmd/Ctrl+B ничего не сворачивает. Уже `SIDEBAR_SHEET_BELOW` (планшет
    // и телефон) меню выезжает поверх контента по кнопке — постоянная колонка
    // съедала треть ширины планшета.
    <SidebarProvider open onOpenChange={() => {}} mobileBreakpoint={SIDEBAR_SHEET_BELOW}>
      <div
        className={cn(
          'fixed inset-0 z-0 pointer-events-none',
          hideSidebar ? 'bg-surface' : 'bg-background',
        )}
      />
      {/* --content-left: где начинается область контента справа от меню. По ней
          лоадер страницы встаёт по центру контента, а не всего окна. `min-h-dvh`, а не
          `min-h-screen`: 100vh на телефоне выше видимой области (см. `pageHeight.ts`). */}
      <div
        className={cn(
          'relative z-10 flex min-h-dvh w-full',
          hideSidebar
            ? null
            : 'pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] lg:[--content-left:var(--sidebar-width)]',
        )}
      >
        {!hideSidebar ? <AppSidebar /> : null}
        {!hideSidebar ? <MobileSidebarTrigger /> : null}
        {/* min-w-0: без него min-width:auto распирает main до min-content
            контента (например, ленты блоков в редакторе) и страница получает
            горизонтальный скролл. `pb-20` на телефоне — место под кнопку меню внизу
            слева (`MobileSidebarTrigger`), иначе она перекрывала кнопки в конце страницы;
            высоту страниц под него — `pageHeight.ts`. */}
        <main className={cn('min-w-0 flex-1', hideSidebar ? null : MOBILE_MENU_PB_CLASS)}>
          <Routes>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="editor/:courseId" element={<Editor />} />
            <Route path="catalog" element={<Catalog />} />
            <Route path="explore" element={<Catalog />} />
            <Route path="course/:courseId" element={<AcademyCourseRedirect />} />
            <Route path="stats/:courseId" element={<CourseStats />} />
            <Route path="course-settings/:courseId" element={<CourseSettings />} />
            <Route path="skills" element={<SkillsPage />} />
            {settingsRoutes}
            <Route
              path="admin/panel"
              element={
                <AdminOnly>
                  <AdminPage />
                </AdminOnly>
              }
            />
            <Route path="instructions" element={<InstructionsAdmin />} />
            <Route path="instructions/new" element={<InstructionEdit />} />
            <Route path="instructions/:slug" element={<InstructionEdit />} />
            {/* Старый адрес (`/admin/...` — сервер тут ни при чём, ответ бэка п.5):
                переадресация, чтобы не сломать закладки и открытые вкладки. */}
            <Route path="admin/instructions/*" element={<LegacyInstructionsRedirect />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </SidebarProvider>
  )
}
